import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

import LoginPage from '@/pages/Login';
import SetPassword from '@/pages/SetPassword';
import Apply from '@/pages/Apply';
import ApplyChain from '@/pages/ApplyChain';
import Welcome from '@/pages/Welcome';
import WelcomeBack from '@/pages/WelcomeBack';
import Home from '@/pages/Home';
import Guide from '@/pages/Guide';
import Resources from '@/pages/Resources';
import ResourceArticle from '@/pages/ResourceArticle';
import Order from '@/pages/Order';
import AllStores from '@/pages/AllStores';
import MyCollection from '@/pages/MyCollection';
import Invoices from '@/pages/Invoices';
import InvoiceDetail from '@/pages/InvoiceDetail';
import Statements from '@/pages/Statements';
import Pay from '@/pages/Pay';
import Payments from '@/pages/Payments';
import Autopay from '@/pages/Autopay';
import Cylinders from '@/pages/Cylinders';
import Equipment from '@/pages/Equipment';
import ServiceRequests from '@/pages/ServiceRequests';
import Settings from '@/pages/Settings';
import ProfileTab from '@/pages/settings/ProfileTab';
import TeamTab from '@/pages/settings/TeamTab';
import NotificationsTab from '@/pages/settings/NotificationsTab';
import PaymentsTab from '@/pages/settings/PaymentsTab';
import SecurityTab from '@/pages/settings/SecurityTab';
import Cart from '@/pages/Cart';
import Confirmation from '@/pages/Confirmation';
import OrderChange from '@/pages/OrderChange';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminQuickOrder from '@/pages/admin/AdminQuickOrder';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import { LandingRedirect } from '@/components/auth/LandingRedirect';
import AdminCustomerDetail from '@/pages/admin/AdminCustomerDetail';
import OverviewTab from '@/pages/admin/customer-detail/OverviewTab';
import DeliveriesTab from '@/pages/admin/customer-detail/DeliveriesTab';
import PricingTab from '@/pages/admin/customer-detail/PricingTab';
import UsersTab from '@/pages/admin/customer-detail/UsersTab';
import BillingTab from '@/pages/admin/customer-detail/BillingTab';
import PaperTab from '@/pages/admin/customer-detail/PaperTab';
import ChangeRequestsTab from '@/pages/admin/customer-detail/ChangeRequestsTab';
import ClosureTab from '@/pages/admin/customer-detail/ClosureTab';
import AdminCustomerClosure from '@/pages/admin/AdminCustomerClosure';
import AdminOnboarding from '@/pages/admin/AdminOnboarding';
import AdminEdi from '@/pages/admin/AdminEdi';
import AdminEdiDetail from '@/pages/admin/AdminEdiDetail';
import AdminOrderDesk from '@/pages/admin/AdminOrderDesk';
import AdminOrderDeskDetail from '@/pages/admin/AdminOrderDeskDetail';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminBilling from '@/pages/admin/AdminBilling';
import AdminPayments from '@/pages/admin/AdminPayments';
import AdminPaymentCase from '@/pages/admin/AdminPaymentCase';
import AdminAudits from '@/pages/admin/AdminAudits';
import AdminActivity from '@/pages/admin/AdminActivity';
import AdminReviews from '@/pages/admin/AdminReviews';
import Review from '@/pages/Review';
import AdminAuditReview from '@/pages/admin/AdminAuditReview';
import AdminKnowledge from '@/pages/admin/AdminKnowledge';
import AdminCompany from '@/pages/admin/AdminCompany';
import DeskLayout from '@/pages/desk/DeskLayout';
import Desk from '@/pages/desk/Desk';
import AuditLayout from '@/pages/audit/AuditLayout';
import AuditHome from '@/pages/audit/AuditHome';
import AuditCustomers from '@/pages/audit/AuditCustomers';
import AuditForm from '@/pages/audit/AuditForm';
import RentalsGate from '@/pages/audit/rentals/RentalsGate';
import RentalsRoster from '@/pages/audit/rentals/RentalsRoster';
import RentalsRun from '@/pages/audit/rentals/RentalsRun';
import RentalsEstimate from '@/pages/audit/rentals/RentalsEstimate';
import RentalsCustomer from '@/pages/audit/rentals/RentalsCustomer';
import RentalsAudits from '@/pages/audit/rentals/RentalsAudits';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Welcome-email landing: verifies the one-time recovery token_hash and
  // shows the password-creation form. Public — the token IS the auth.
  { path: '/set-password', element: <SetPassword /> },
  // Public self-serve onboarding form — NO auth, NO app shell. Sibling of /login.
  { path: '/apply', element: <Apply /> },
  // The CHAIN new-store form. One link per chain (minted on the group master's
  // Overview tab); the token is the only credential, so nothing is prefilled.
  { path: '/apply/:token', element: <ApplyChain /> },
  // Public "claim your existing account" front door (switch-on + cutover emails
  // link here). NO auth, NO app shell.
  { path: '/welcome-back', element: <WelcomeBack /> },
  { path: '/review/:token', element: <Review /> },
  // First-run onboarding wizard — auth-gated but NOT the AppShell (kept outside
  // so the AppShell onboarding diversion can't loop). Save payment + quick tour.
  {
    path: '/welcome',
    element: (
      <ProtectedRoute>
        <Welcome />
      </ProtectedRoute>
    ),
  },
  // Driver Cylinder Audit PWA — auth-gated but NOT the customer AppShell
  // (drivers hold no customer membership; AuditLayout gates on is_driver).
  {
    path: '/audit',
    element: (
      <ProtectedRoute>
        <AuditLayout />
      </ProtectedRoute>
    ),
    children: [
      // Landing: two cards for staff; a driver-only login is sent straight
      // to the customer picker at /audit/count.
      { index: true, element: <AuditHome /> },
      // ⚠ 'count' is declared ABOVE ':customerId' so it is never read as an id.
      { path: 'count', element: <AuditCustomers /> },
      // CO₂ tank rentals — the office's section of the audit portal
      // (superadmin only, RentalsGate). ⚠ Declared ABOVE ':customerId' so
      // "rentals" is never read as a customer id.
      {
        path: 'rentals',
        element: <RentalsGate />,
        children: [
          { index: true, element: <RentalsRoster /> },
          { path: 'run', element: <RentalsRun /> },
          { path: 'estimate', element: <RentalsEstimate /> },
          { path: 'audits', element: <RentalsAudits /> },
          { path: ':qboId', element: <RentalsCustomer /> },
        ],
      },
      // An audit at an account that exists only in QuickBooks (no portal row) —
      // opened from the rental account's page. ⚠ Above ':customerId' so "qbo"
      // is never read as a customer id.
      { path: 'qbo/:qboId', element: <AuditForm /> },
      { path: ':customerId', element: <AuditForm /> },
    ],
  },
  // Phone desk — the live receptionists' page. Auth-gated but NOT the customer
  // AppShell and NOT /admin: a phone-desk login sees this page and nothing else
  // (DeskLayout gates on is_phone_desk; every desk-* function re-checks).
  {
    path: '/desk',
    element: (
      <ProtectedRoute>
        <DeskLayout />
      </ProtectedRoute>
    ),
    children: [{ index: true, element: <Desk /> }],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      // Staff (superadmins) land in the admin portal; customers land on /home.
      { index: true, element: <LandingRedirect /> },
      { path: 'home',                 element: <Home /> },
      { path: 'guide',                element: <Guide /> },
      // Customer help library — every customer-visible KB doc + how-to videos.
      { path: 'resources',            element: <Resources /> },
      { path: 'resources/:slug',      element: <ResourceArticle /> },
      { path: 'order',                element: <Order /> },
      // Chain / franchise rollup — every store the signed-in user's group
      // master gives them, with live balances + one-click store switching.
      // The sidebar entry only renders for users who belong to a group.
      { path: 'stores',               element: <AllStores /> },
      { path: 'collection',           element: <MyCollection /> },
      { path: 'cart',                 element: <Cart /> },
      { path: 'confirmation',         element: <Confirmation /> },
      // Stable, shareable URL embedded in confirmation emails.
      // Customer can return here weeks later to check status.
      { path: 'orders/:order_id',     element: <Confirmation /> },
      // "Change this order" — rebuilds the order as editable cart-style rows
      // and emails the DIFF to service. Deliberately NOT the shopping cart:
      // Service Fusion has no write API for jobs, so a change is a request a
      // human applies to the existing job, and routing it through the cart
      // would leave Review-&-submit one click from a duplicate SF job.
      { path: 'orders/:order_id/change', element: <OrderChange /> },
      // Old "Orders" page — folded into the Shop page's Order-history tab.
      // Kept as a redirect so existing links/bookmarks still resolve.
      { path: 'activity',             element: <Navigate to="/order?tab=history" replace /> },
      { path: 'invoices',             element: <Invoices /> },
      // Customer-facing e-statement (open items + aging) — self-serve.
      { path: 'statements',           element: <Statements /> },
      // Our own multi-invoice pay page (select open invoices, pay together).
      { path: 'pay',                  element: <Pay /> },
      { path: 'payments',             element: <Payments /> },
      // Autopay: save a card + put rentals on automatic payment.
      { path: 'autopay',              element: <Autopay /> },
      // Legacy redirect — Bill & Pay portal is gone.
      { path: 'pay-portal',           element: <Navigate to="/invoices" replace /> },
      { path: 'invoices/:invoice_id', element: <InvoiceDetail /> },
      { path: 'cylinders',            element: <Cylinders /> },
      { path: 'equipment',            element: <Equipment /> },
      { path: 'service',              element: <ServiceRequests /> },
      // Old standalone /account page — folded into Settings → Profile.
      // Redirect kept so existing links/emails still resolve.
      { path: 'account',              element: <Navigate to="/settings/profile" replace /> },
      // Customer self-serve Settings hub — tabbed (Profile / Team /
      // Notifications / Security). Management controls gate on customer-admin
      // role server-side; members see read-only.
      {
        path: 'settings',
        element: <Settings />,
        children: [
          { index: true, element: <Navigate to="/settings/profile" replace /> },
          { path: 'profile',       element: <ProfileTab /> },
          { path: 'team',          element: <TeamTab /> },
          { path: 'notifications', element: <NotificationsTab /> },
          { path: 'payments',      element: <PaymentsTab /> },
          { path: 'security',      element: <SecurityTab /> },
        ],
      },
      {
        path: 'admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/quick-order" replace /> },
          // Phone-order launchpad: pick a customer → act as them → Shop.
          { path: 'quick-order', element: <AdminQuickOrder /> },
          { path: 'customers',  element: <AdminCustomers /> },
          // Account closure for a QuickBooks customer with NO portal row
          // (Refractor's INACTIVATE button lands here).
          { path: 'customers/qbo/:qboId/closure', element: <AdminCustomerClosure /> },
          // Per-customer detail page with tabbed sub-navigation.
          {
            path: 'customers/:customerId',
            element: <AdminCustomerDetail />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <OverviewTab /> },
              // Locations, delivery days and order reminders are ONE page
              // (sub-tabs, ?view=days|reminders); /deliveries is the old address.
              { path: 'locations', element: <DeliveriesTab /> },
              { path: 'deliveries', element: <Navigate to="../locations?view=days" replace /> },
              { path: 'pricing', element: <PricingTab /> },
              { path: 'users', element: <UsersTab /> },
              { path: 'billing', element: <BillingTab /> },
              { path: 'paper', element: <PaperTab /> },
              { path: 'requests', element: <ChangeRequestsTab /> },
              // Guided account-closure status page (equipment -> tanks ->
              // balances -> refunds -> superadmin close).
              { path: 'closure', element: <ClosureTab /> },
            ],
          },
          // Applications + chain/franchise links live on one page now
          // (Onboarding's "Chain & franchise links" section) — /admin/links
          // stays as a redirect so old bookmarks and any hardcoded link keep
          // working.
          { path: 'onboarding', element: <AdminOnboarding /> },
          { path: 'links', element: <Navigate to="/admin/onboarding?tab=links" replace /> },
          // EDI email-order review queue: POs emailed to a chain's inbound
          // address land here for review/submit (per-customer settings live
          // on the Billing & comms tab).
          { path: 'edi',           element: <AdminEdi /> },
          { path: 'edi/:intakeId', element: <AdminEdiDetail /> },
          { path: 'order-desk',            element: <AdminOrderDesk /> },
          { path: 'order-desk/:intakeId',  element: <AdminOrderDeskDetail /> },
          // Bulk statement Billing Run console.
          { path: 'billing',   element: <AdminBilling /> },
          // Bill & Pay → QuickBooks payment cutover console (processor per
          // customer, bridge/flip/deactivate actions, ACH returns).
          { path: 'payments',  element: <AdminPayments /> },
          // One payment ticket on a returned / disputed payment.
          { path: 'payments/cases/:caseId', element: <AdminPaymentCase /> },
          // Tank Rental Audit oversight + driver-access management.
          { path: 'audits',    element: <AdminAudits /> },
          { path: 'activity',  element: <AdminActivity /> },
          { path: 'reviews',   element: <AdminReviews /> },
          // Single-audit review page — the shortage-approval email links here.
          { path: 'audits/:auditId', element: <AdminAuditReview /> },
          // Mr. Bubbles knowledge base — self-serve docs, auto-indexed on save.
          { path: 'knowledge', element: <AdminKnowledge /> },
          // The cross-app staff manager UI moved to the gateway hub
          // (alamedapointbg.com/admin.html), which drives our admin-staff API.
          // Retired standalone Users tab — redirects into the per-customer
          // Users tab. Route kept so old /admin/users?customer=… links resolve.
          { path: 'users',      element: <AdminUsers /> },
          { path: 'company',   element: <AdminCompany /> },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/home" replace /> },
]);
